// Copyright (c) 2025 WiseTime. All rights reserved.

import { Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { showSnackBar } from "../../snackbar/actions"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as Actions from "../actions"
import {
  DeleteTimesheetFieldRequestSchema,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { Code, executorMutation, Init, isConnectError, timesheetFieldService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"
import type { Empty } from "@bufbuild/protobuf/wkt"

type DeleteTimesheetFieldRpc = (request: Init<typeof DeleteTimesheetFieldRequestSchema>) => Observable<Empty>

export const deleteTimesheetField = (deleteTimesheetFieldRpc: DeleteTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTimesheetField.match),
      map(action => action.payload),
      mergeMap(payload =>
        deleteTimesheetFieldRpc(payload.request).pipe(
          map(() => {
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            return TimelineSlice.deleteTimesheetField({
              id: payload.request.id ?? "",
            })
          }),
          catchError(e => handleError(e, payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.Payload<Init<typeof DeleteTimesheetFieldRequestSchema>>): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return of(TimelineSlice.deleteTimesheetField({
        id: payload.request.id ?? "",
      }))
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_delete_timesheet_field_permission_denied",
          message: "Failed to delete timesheet field. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_delete_timesheet_field_failed",
      message: "Failed to delete timesheet field. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const deleteTimesheetFieldRpc = (request: Init<typeof DeleteTimesheetFieldRequestSchema>): Observable<Empty> =>
  executorMutation(() => timesheetFieldService.deleteTimesheetField(create(DeleteTimesheetFieldRequestSchema, request)))

export default deleteTimesheetField(deleteTimesheetFieldRpc)
